import React, { useEffect, useState } from "react";
import { Alert, ListGroup, Spinner } from "react-bootstrap";
import { ErrorResponse } from "@core/types/errorResponse";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import StatusChangeConfirmModal from "@features/admin/finance/components/StatusChangeConfirmModal";
import { useFinanceDeductibleAction } from "@features/admin/finance/deductible/hooks/useFinanceDeductibleAction";
import { fetchFinanceDeductibleImpactAsync } from "@features/admin/finance/deductible/services/financeDeductibleSvc";
import {
    FinanceDeductible,
    FinanceDeductibleConflict,
    FinanceDeductibleImpact,
} from "@features/admin/finance/deductible/types/financeDeductible";

export type DeductibleStatusKind = "deactivate" | "reactivate";

interface DeductibleStatusModalProps {
    deductible: FinanceDeductible | null;
    kind: DeductibleStatusKind;
    onClose: () => void;
    onChanged: () => void | Promise<void>;
}

const UNPROCESSABLE = 422;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Conflicts sent back by enable when the configuration changed after the impact check. */
export const conflictsFromError = (error: ErrorResponse | null | undefined): FinanceDeductibleConflict[] | null => {
    if (!error || error.statusCode !== UNPROCESSABLE) return null;
    const conflicts = (error.errors as unknown as Record<string, unknown> | undefined)?.conflicts;
    return Array.isArray(conflicts) ? (conflicts as FinanceDeductibleConflict[]) : null;
};

const ConflictList: React.FC<{ conflicts: FinanceDeductibleConflict[] }> = ({ conflicts }) => (
    <ListGroup className="mt-2" data-testid="deductible-conflict-list">
        {conflicts.map((c) => (
            <ListGroup.Item key={`${c.type}-${c.id}`} className="small d-flex justify-content-between">
                <span>
                    {c.name} <span className="text-muted">({c.type === "category" ? "category" : "allocation rule"})</span>
                    {c.is_active === false && <span className="text-muted"> (inactive)</span>}
                </span>
                <span className="text-danger">{c.percentage_total}%</span>
            </ListGroup.Item>
        ))}
    </ListGroup>
);

const DeductibleStatusModal: React.FC<DeductibleStatusModalProps> = ({ deductible, kind, onClose, onChanged }) => {
    const nationId = useScopeId() ?? 0;
    const [impact, setImpact] = useState<FinanceDeductibleImpact | null>(null);
    const [conflicts, setConflicts] = useState<FinanceDeductibleConflict[]>([]);
    const [loading, setLoading] = useState(false);
    const [impactFailed, setImpactFailed] = useState(false);
    const showError = useApiError();
    const { busy, doDisable, doEnable } = useFinanceDeductibleAction();

    useEffect(() => {
        setImpact(null);
        setImpactFailed(false);
        setConflicts([]);
        if (!deductible) return;
        let cancelled = false;
        setLoading(true);
        fetchFinanceDeductibleImpactAsync(nationId, deductible.deductible_id)
            .then((response) => {
                if (cancelled) return;
                if (!response.isSuccess()) {
                    setImpactFailed(true);
                    return;
                }
                const data = response.getData();
                setImpact(data);
                setConflicts(data?.conflicts ?? []);
            })
            .catch(() => { if (!cancelled) setImpactFailed(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [deductible, nationId]);

    const handleConfirm = async () => {
        if (!deductible) return;
        try {
            const response = kind === "deactivate"
                ? await doDisable(nationId, deductible.deductible_id)
                : await doEnable(nationId, deductible.deductible_id, conflicts.length > 0);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "");
                onClose();
                await onChanged();
                return;
            }

            const error = response.getError() as ErrorResponse;
            const refreshed = kind === "reactivate" ? conflictsFromError(error) : null;
            if (refreshed) {
                setConflicts(refreshed);
                return;
            }
            showError(error);
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    const usage = impact
        ? `Configured on ${plural(impact.usage.categories, "category", "categories")} and ${plural(impact.usage.rules, "rule", "rules")}.`
        : "";
    const inUse = !!impact && (impact.usage.categories > 0 || impact.usage.rules > 0);

    const body = (() => {
        if (impactFailed) {
            return (
                <Alert variant="danger" className="mb-0" data-testid="deductible-impact-error">
                    Couldn't check where this deductible is used. Close and try again.
                </Alert>
            );
        }
        if (loading || !impact) {
            return (
                <div className="d-flex align-items-center small text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />Checking where this deductible is used…
                </div>
            );
        }
        if (kind === "deactivate") {
            return (
                <div data-testid="deductible-deactivate-body">
                    <p className="mb-2">Deactivate <strong>{deductible?.name}</strong>?</p>
                    <p className="mb-0">
                        {usage}
                        {inUse && " They stop calculating it from now; configurations stay and become locked."}
                    </p>
                </div>
            );
        }
        return (
            <div data-testid="deductible-reactivate-body">
                <p className="mb-2">Reactivate <strong>{deductible?.name}</strong>?</p>
                <p className="mb-0">
                    {usage}
                    {inUse && " Its locked configurations start calculating again from now."}
                </p>
                {conflicts.length > 0 && (
                    <Alert variant="warning" className="mt-3 mb-0" data-testid="deductible-reactivate-conflicts">
                        Reactivating would push the percentages on these categories or allocation rules above 100%,
                        including inactive ones. You can reactivate and set No allocation for this deductible on
                        them, or cancel.
                        <ConflictList conflicts={conflicts} />
                    </Alert>
                )}
            </div>
        );
    })();

    return (
        <StatusChangeConfirmModal
            show={!!deductible}
            kind={kind === "deactivate" ? "disable" : "enable"}
            entityLabel="deductible"
            entityName={deductible?.name ?? ""}
            title={kind === "deactivate" ? "Deactivate Deductible" : "Reactivate Deductible"}
            bodyText={body}
            confirmLabel={
                kind === "deactivate"
                    ? "Yes, deactivate"
                    : conflicts.length > 0 ? "Reactivate and set No allocation on these" : "Yes, reactivate"
            }
            confirmDisabled={loading || !impact}
            onConfirm={handleConfirm}
            onCancel={() => { if (!busy) onClose(); }}
            busy={busy}
        />
    );
};

export default DeductibleStatusModal;
